import React from "react";
import axios from "axios";
import { Table, Button } from "react-bootstrap";
import { useEffect, useState } from "react";
import GLOBALVAR from "../constants";
import { format } from 'date-fns';
import LoadingSpinner from "../components/misc/LoadingSpinner";
import jsPDF from "jspdf";
import 'jspdf-autotable';
import './css/ReportsStyles.scss'; 
import ToPeso from "../components/misc/ToPeso";
import logo from "../logo.png";
import calculateDueDates from "../components/misc/calculateDueDates";
import calculatePayments from "../components/misc/calculatePayments";

function ReportLoansOutstandingBalanceSheet() {
    const [paymentHeaders, setPaymentHeaders] = useState([]);
    const [paymentLists, setPaymentLists] = useState([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [count, setCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [endDate, setEndDate] = useState('');
    const [totals, setTotals] = useState({
        subtotalCount: 0,
        subtotalAmountGranted: 0,
        subtotalTotalPayments: 0,
        subtotalTotalBalance: 0,
        grandTotalCount: 0,
        grandTotalAmountGranted: 0,
        grandTotalTotalPayments: 0,
        grandTotalTotalBalance: 0,
    });

    const fetchPayments = async (currentPage, itemsPerPage, endDate) => {
        setShowSpinner(true);

        try {
            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/fetch/all/outstanding`, {
                params: {
                    page: currentPage,
                    limit: itemsPerPage,
                    endDate: endDate || format(new Date(), 'yyyy-MM-dd')
                }
            });

            const loans = response.data.rows.sort((a, b) => 
                new Date(b.dateGranted).getTime() - new Date(a.dateGranted).getTime()
            );


            const loansData = loans.reduce((acc, item) => {
                const fullName = `${item.applicant.lastName}, ${item.applicant.firstName} ${item.applicant.middleName}`;
                const fullNameWithLink = (
                    <a href={`/applicant/${item.applicant.id}`}>
                        {fullName}
                    </a>
                );

                const loansnum = item.id.toString().padStart(6, '0');
                const loansNumWithLink = (
                    <a href={`/loans/${item.id}`}>
                        {loansnum}
                    </a>
                );

                const dates = calculateDueDates(item.dateGranted, item.loanInterval, item.loanTerm, item.amountGranted, item.interestRate, item.advanceInterest);
                const paymentsArr = calculatePayments(item.dateGranted, item.loanInterval, item.loanTerm, item.amountGranted, item.interestRate, item.payments, item.advanceInterest);
                const textdates = `${dates[0].date} - ${dates[dates.length-1].date}`;
                const paymentDate = (paymentsArr.paymentDate) ? format(paymentsArr.paymentDate, 'MM/dd/yyyy') : '';

                // Convert dateGranted to timestamp
                const dateTimestamp = new Date(item.dateGranted).getTime() + parseInt(item.id);
                
                // Use timestamp as the key
                acc[dateTimestamp] = {
                    items: [
                        { key: 'fullName', value: fullNameWithLink },
                        { key: 'loanProcessingID', value: loansNumWithLink },
                        { key: 'dueDates', value: textdates },
                        { key: 'amountGranted', value: <ToPeso amount={item.amountGranted} />, actual: parseFloat(item.amountGranted) },
                        { key: 'totalPaid', value: <ToPeso amount={paymentsArr.totalPaid} />, actual: parseFloat(paymentsArr.totalPaid)  },
                        { key: 'totalBalance', value: <ToPeso amount={paymentsArr.remainingBalance} />, actual: parseFloat(paymentsArr.remainingBalance) },
                        { key: 'loanInterval', value: item.loanInterval },
                        { key: 'LastTransaction', value: paymentDate },
                    ]
                };
                return acc;
            }, {});

            setCount(response.data.count);
            setPaymentLists(loansData);

            // Calculate subtotals
            const subtotalCount = Object.keys(loansData).length;
            const subtotalAmountGranted = Object.values(loansData).reduce((sum, item) => sum + item.items.find(i => i.key === 'amountGranted').actual, 0);
            const subtotalTotalPayments = Object.values(loansData).reduce((sum, item) => sum + item.items.find(i => i.key === 'totalPaid').actual, 0);
            const subtotalTotalBalance = Object.values(loansData).reduce((sum, item) => sum + item.items.find(i => i.key === 'totalBalance').actual, 0);

            // Set subtotals
            setTotals(prevTotals => ({
                ...prevTotals,
                subtotalCount,
                subtotalAmountGranted,
                subtotalTotalPayments,
                subtotalTotalBalance
            }));

            // Set grand totals from API response
            setTotals(prevTotals => ({
                ...prevTotals,
                grandTotalCount: response.data.grandTotals.totalNumberOfLoans,
                grandTotalAmountGranted: response.data.grandTotals.totalAmountGranted,
                grandTotalTotalPayments: response.data.grandTotals.totalPayments,
                grandTotalTotalBalance: response.data.grandTotals.totalRemainingBalance
            }));

            const paymentHeaders = [
                { value: "Full Name" },
                { value: "Loan No." },
                { value: "Due Dates" },
                { value: "Amount Granted" },
                { value: "Total Payments" },
                { value: "Total Balance" },
                { value: "Loan Interval" },
                { value: "Last transaction" }
            ];

            setPaymentHeaders(paymentHeaders);
            setShowSpinner(false);
        } catch (error) {
            console.error('Error fetching payments:', error);
            setShowSpinner(false);
        }
    };

    const btnPrev = () => {
        setCurrentPage(prevPage => prevPage - 1);
    };

    const btnNext = () => {
        setCurrentPage(prevPage => prevPage + 1);
    };

    const btnPage = (page) => {
        setCurrentPage(page);
    };

    const btnFirst = () => {
        setCurrentPage(1);
    };

    const btnLast = () => {
        const totalPages = Math.ceil(count / itemsPerPage);
        setCurrentPage(totalPages);
    };

    const totalPages = Math.ceil(count / itemsPerPage);

    const getPageNumbers = () => {
        const pageNumbers = [];
        const totalPagesToShow = 3;

        if (totalPages <= totalPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            const lastPage = currentPage + 1 > totalPages ? totalPages : currentPage + 1;
            const firstPage = lastPage - (totalPagesToShow - 1) > 0 ? lastPage - (totalPagesToShow - 1) : 1;
            const dots = currentPage + 1 < totalPages ? '...' : '';

            for (let i = firstPage; i <= lastPage; i++) {
                pageNumbers.push(i);
            }

            if (dots) {
                pageNumbers.push(dots);
            }

            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };

    useEffect(() => {
        fetchPayments(currentPage, itemsPerPage, endDate);
    }, [currentPage, itemsPerPage, endDate]); // Call fetchPayments whenever currentPage, itemsPerPage, or endDate changes

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1); // Reset to the first page whenever itemsPerPage changes
    };

    const handleGeneratePDF = async () => {
        setShowSpinner(true);
        try {
            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/fetch/all/outstanding`, {
                params: {
                    page: 1,
                    limit: count, // Fetch all results
                    endDate: endDate || format(new Date(), 'yyyy-MM-dd')
                }
            });
    
            const doc = new jsPDF('landscape');
    
            // Function to center text
            const centerText = (text, y, fontWeight = 'normal') => {
                // Set font style
                doc.setFont(undefined, fontWeight);
    
                // Get page width
                const pageWidth = doc.internal.pageSize.getWidth();
                // Calculate the text width
                const textWidth = doc.getTextDimensions(text).w;
                // Calculate the text's X coordinate to be centered
                const textX = (pageWidth - textWidth) / 2;
                // Add the text to the document
                doc.text(text, textX, y);
            }
    
            const formatDate = (date) => {
                const d = new Date(date);
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const year = d.getFullYear();
                return `${month}/${day}/${year}`;
            }
    
            // Add company logo
            const logoImg = await fetch(logo);
            const logoBlob = await logoImg.blob();
            const logoURL = URL.createObjectURL(logoBlob);
            
            // Adjust logo dimensions to keep width under 2 points with proper aspect ratio
            const logoWidth = 50; // Just under 2 points
            const aspectRatio = 60 / 12.30; // Calculate original aspect ratio
            const logoHeight = logoWidth / aspectRatio; // Maintain aspect ratio

            doc.addImage(logoURL, 'PNG', 10, 3, logoWidth, logoHeight);
    
            // Add document heading
            doc.setFontSize(14);
            centerText("Loans Outstanding Balance Sheet", 10, 'bold');
    
            if (endDate) {
                doc.setFontSize(12);
                centerText(`As of ${formatDate(endDate)}`, 19);
            }
    
            const tableColumn = paymentHeaders.map(header => header.value);
            const tableRows = [];
    
            response.data.rows.forEach(item => {
                const dates = calculateDueDates(item.dateGranted, item.loanInterval, item.loanTerm, item.amountGranted, item.interestRate, item.advanceInterest);
                const paymentsArr = calculatePayments(item.dateGranted, item.loanInterval, item.loanTerm, item.amountGranted, item.interestRate, item.payments, item.advanceInterest);
                const textdates = `${dates[0].date} - ${dates[dates.length - 1].date}`;
                const paymentDate = (paymentsArr.paymentDate) ? format(paymentsArr.paymentDate, 'MM/dd/yyyy') : '';
    
                const rowData = [
                    `${item.applicant.lastName}, ${item.applicant.firstName} ${item.applicant.middleName}`,
                    item.id.toString().padStart(6, '0'),
                    textdates,
                    parseFloat(item.amountGranted).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    parseFloat(paymentsArr.totalPaid).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    parseFloat(paymentsArr.remainingBalance).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                    item.loanInterval,
                    paymentDate
                ];
    
                tableRows.push(rowData);
            });

            // Add grand totals to PDF below the table
            const grandTotalTableData = [
                'Grand Total', 
                totals.grandTotalCount,
                '',
                totals.grandTotalAmountGranted.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                totals.grandTotalTotalPayments.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                totals.grandTotalTotalBalance.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                '',
                ''
            ];

            tableRows.push(grandTotalTableData);
    
            // Generate the table with data
            doc.autoTable(tableColumn, tableRows, { 
                startY: 30,
                didParseCell: function (data) {
                    const rowIndex = data.row.index;
                    if (rowIndex === tableRows.length - 1) {
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            }); // Adjust startY position as needed
    
    
            // Get current timestamp and format it
            const now = new Date();
            const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
    
            doc.save(`Loans_Outstanding_Balance_Sheet_${timestamp}.pdf`); // Save with timestamp in filename
            setShowSpinner(false);
        } catch (error) {
            console.error('Error generating PDF:', error);
            setShowSpinner(false);
        }
    };    
    
    const handleClick = (e) => {
        e.preventDefault();
        handleGeneratePDF();
    };

    return (
        <>
            {showSpinner && (<LoadingSpinner />)}
            <div className="filters-container">
                <div className="pagination-controls">
                    <label htmlFor="itemsPerPage">Show: </label>
                    <select id="itemsPerPage" value={itemsPerPage} onChange={handleItemsPerPageChange}>
                        <option value={10}>10</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={500}>500</option>
                        <option value={1000}>1000</option>
                    </select>
                </div>
                <div className="date-range-controls">
                    <label htmlFor="endDate">As of: </label>
                    <input
                        type="date"
                        id="endDate"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>
                <Button href="#" onClick={handleClick} className="generate-pdf-button">
                    <i className="fa-solid fa-print"></i> Export
                </Button>
            </div>
            <Table className="list-container" hover responsive>
                <thead className="list-title">
                    <tr>
                        {
                            paymentHeaders.map((item, index) => {
                                return (
                                    <th className="list-header-item" key={index}>{item.value}</th>
                                );
                            })
                        }
                    </tr>
                </thead>
                <tbody>
                    {
                        Object.values(paymentLists).map((item, index) => (
                            <tr className="list-body" key={index}>
                                {item.items.map((subitem, subindex) => (
                                    <td className="list-body-item" key={`${index}+${subindex}`}>
                                        {subitem.value}
                                    </td>
                                ))}
                            </tr>
                        ))
                    }
                </tbody>
                <tfoot>
                    <tr className="subtotal-row">
                        <td>Subtotal</td>
                        <td>{totals.subtotalCount}</td>
                        <td></td>
                        <td><ToPeso amount={totals.subtotalAmountGranted} /></td>
                        <td><ToPeso amount={totals.subtotalTotalPayments} /></td>
                        <td><ToPeso amount={totals.subtotalTotalBalance} /></td>
                        <td></td>
                        <td></td>
                    </tr>
                    <tr className="grand-total-row">
                        <td>Grand Total</td>
                        <td>{totals.grandTotalCount}</td>
                        <td></td>
                        <td><ToPeso amount={totals.grandTotalAmountGranted} /></td>
                        <td><ToPeso amount={totals.grandTotalTotalPayments} /></td>
                        <td><ToPeso amount={totals.grandTotalTotalBalance} /></td>
                        <td></td>
                        <td></td>
                    </tr>
                    <tr className="page-buttons">
                        <td colSpan="12">
                            <div className="page-buttons__container">
                                <button onClick={btnFirst} disabled={currentPage === 1}>
                                    <i className="fa-solid fa-angles-left"></i>
                                </button>
                                <button onClick={btnPrev} disabled={currentPage === 1}>
                                    <i className="fa-solid fa-angle-left"></i>
                                </button>
                                {getPageNumbers().map((page, index) => (
                                    <React.Fragment key={index}>
                                        {page === '...' ? (
                                            <span className="elip">
                                                <i className="fa-solid fa-ellipsis"></i>
                                            </span>
                                        ) : (
                                            <span className="number">
                                                <button onClick={() => btnPage(page)} disabled={currentPage === page}>
                                                    {page}
                                                </button>
                                            </span>
                                        )}
                                    </React.Fragment>
                                ))}
                                <button onClick={btnNext} disabled={currentPage === totalPages}>
                                    <i className="fa-solid fa-angle-right"></i>
                                </button>
                                <button onClick={btnLast} disabled={currentPage === totalPages}>
                                    <i className="fa-solid fa-angles-right"></i>
                                </button>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </Table>
        </>
    );
}

export default ReportLoansOutstandingBalanceSheet;
